import { motion, AnimatePresence } from "motion/react";
import type { Video } from "@/types/video";
import { bilibiliUrl, formatDate, formatNumber } from "@/services/api";
import { usePersistentStore } from "@/stores/playerStore";

interface InfoPanelProps {
  video: Video | null;
  open: boolean;
  onClose: () => void;
  onChangeChannel: () => void;
  onBlockTag: (tag: string) => void;
}

export default function InfoPanel({ video, open, onClose, onChangeChannel, onBlockTag }: InfoPanelProps) {
  const isFav = usePersistentStore((state) => video ? state.favorites.some((item) => item.bvid === video.bvid) : false);
  const toggleFavorite = usePersistentStore((state) => state.toggleFavorite);
  const blockedTags = usePersistentStore((state) => state.blockedTags);

  return (
    <AnimatePresence>
      {open && video && (
        <>
          <motion.div className="absolute inset-0 bg-void/60 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-ui-zone />
          <motion.aside className="absolute right-0 top-0 bottom-0 w-full sm:w-[420px] z-[100] glass-panel info-panel p-6 sm:p-8 overflow-y-auto safe-top safe-bottom" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.32, ease: "easeOut" }} data-ui-zone>
            <div className="flex justify-between items-start mb-8">
              <div><div className="vfd text-xs tracking-widest2">CH {String(video.channel).padStart(3, "0")}</div><div className="text-[10px] tracking-widest2 text-[#65476e] mt-1">SIGNAL IDENTIFIED</div></div>
              <button onClick={onClose} className="font-mono text-xs tracking-widest2 text-[#4c3156] hover:text-signal transition-colors" aria-label="关闭">CLOSE [ESC]</button>
            </div>

            <div className="space-y-5 font-mono">
              <Field label="标题" value={video.title} highlight />
              <Field label="UP" value={video.author.name ?? "未知"} />
              <Field label="发布日期" value={formatDate(video.pubdate)} />
              <Field label="播放量" value={`${formatNumber(video.view)} 次`} />
              <Field label="点赞" value={formatNumber(video.like)} />
              <Field label="分区" value={video.category ?? "未分类"} />
              <Field label="BV" value={video.bvid} mono />
            </div>

            <section className="mt-7 pt-5 border-t border-[#d995b8]">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-bold text-sm text-[#2f193a]">视频标签</h2>
                <span className="text-[10px] text-[#65476e]">点击标签即可屏蔽</span>
              </div>
              {video.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3">
                  {video.tags.map((tag) => {
                    const blocked = blockedTags.includes(tag);
                    return <button key={tag} onClick={() => !blocked && onBlockTag(tag)} title={blocked ? "已屏蔽" : `屏蔽 ${tag}`} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${blocked ? "border-[#9f8aa4] bg-[#eee5ef] text-[#725978] line-through" : "border-[#b93172] bg-white text-[#35203e] shadow-[0_1px_0_rgba(185,49,114,.2)] hover:bg-signal hover:text-white hover:border-signal"}`}>#{tag}</button>;
                  })}
                </div>
              ) : <p className="mt-3 text-xs text-[#65476e]">这个旧视频没有可用标签。</p>}
            </section>

            <div className="mt-8 flex flex-col gap-3">
              <button onClick={onChangeChannel} className="w-full font-mono tracking-widest2 py-3 border border-[#8d5474] text-[#35203e] hover:border-signal hover:text-signal transition-colors">[ 换台 ]</button>
              <button onClick={() => toggleFavorite({ bvid: video.bvid, title: video.title, year: video.year, category: video.category, author: video.author.name, addedAt: Date.now() })} className={`w-full font-mono tracking-widest2 py-3 border transition-colors ${isFav ? "border-signal text-signal" : "border-[#8d5474] text-[#35203e] hover:border-signal hover:text-signal"}`}>{isFav ? "♥ 已收藏" : "♡ 收藏"}</button>
              <a href={bilibiliUrl(video.bvid)} target="_blank" rel="noopener noreferrer" className="w-full text-center font-mono tracking-widest2 py-3 border border-[#8d5474] text-[#4c3156] hover:text-signal hover:border-signal transition-colors">在 Bilibili 打开 ↗</a>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, highlight, mono }: { label: string; value: string; highlight?: boolean; mono?: boolean }) {
  return <div className="space-y-1"><div className="text-[10px] uppercase tracking-widest2 text-[#8d6c89]">{label}</div><div className={`text-sm leading-relaxed ${highlight ? "font-semibold text-[#26102f]" : "text-[#51395f]"} ${mono ? "text-xs break-all" : ""}`}>{value}</div></div>;
}
